import { Fragment } from 'react'
import { css } from '@emotion/react'
import { Text } from 'components/Typography'
import { BREAKPOINTS, pxToRem } from 'utils/styled'
import { FormFieldset } from 'components/FormButton'
import { Fieldset } from 'components/fieldset/Fieldset'
import { useForm } from 'react-hook-form'
import { Stack } from 'components/Stack'
import { postCustomSiteRequest } from 'api/goodtrust/site'
import { handleAndToastError, toastSuccess } from 'components/Toast'
import { ModalTitle } from 'components/modal/Modal'
import { Button } from 'components/button/Button'
import { useFieldValidation } from 'utils/i18n/useFieldValidation'
import { useDynamicTranslations } from 'utils/i18n/useDynamicTranslations'
import { BlockSpinner } from 'components/spinner/Spinner'

interface FormFields {
  name: string
  email: string
  url: string
  action: string
  note?: string
}

export function OrderRequestModal(props: { className?: string; onClose: () => void }) {
  const { t, isLoading } = useDynamicTranslations('device')

  const validation = useFieldValidation()
  const form = useForm<FormFields>()

  const handleSubmit = async (payload: FormFields) => {
    try {
      const res = await postCustomSiteRequest({
        name: payload.name,
        email: payload.email,
        url: payload.url,
        action: payload.action,
        note: payload.note,
      })

      if (!res.ok) throw Error('Failed to create a site request: ' + res.error)

      toastSuccess('site_request_sent')
      props.onClose()
    } catch (err) {
      handleAndToastError(err, 'failed_to_create_site_request')
    }
  }
  if (isLoading) return <BlockSpinner />
  return (
    <Fragment>
      <ModalTitle>{t('device.order_req_modal.title')}</ModalTitle>
      <Text css={{ marginBottom: `${pxToRem(24)}rem` }}>{t('device.order_req_modal.desc')}</Text>

      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack space={14}>
          <FormFieldset form={form}>
            <Fieldset>
              <input {...form.register('name', validation.required())} placeholder="&nbsp;" />
              <label>{t('device.order_req_modal.name_label')}</label>
            </Fieldset>
          </FormFieldset>
          <FormFieldset form={form}>
            <Fieldset>
              <input
                {...form.register('email', {
                  ...validation.required(),
                  ...validation.email(),
                })}
                type="email"
                placeholder="&nbsp;"
              />
              <label>{t('device.order_req_modal.email_label')}</label>
            </Fieldset>
          </FormFieldset>
          <FormFieldset form={form}>
            <Fieldset>
              <input {...form.register('url', validation.required())} placeholder="&nbsp;" />
              <label>{t('device.order_req_modal.site_label')}</label>
            </Fieldset>
          </FormFieldset>
          <FormFieldset form={form}>
            <Fieldset>
              <input {...form.register('action', validation.required())} placeholder="&nbsp;" />
              <label>{t('device.order_req_modal.action_label')}</label>
            </Fieldset>
          </FormFieldset>
          <FormFieldset form={form}>
            <Fieldset>
              <textarea
                {...form.register('note')}
                placeholder="&nbsp;"
                css={{ resize: 'vertical', minHeight: 160 }}
              />
              <label>{t('device.order_req_modal.note_label')}</label>
            </Fieldset>
          </FormFieldset>
        </Stack>

        <div
          css={css`
            width: 50%;
            margin-left: auto;
            display: grid;
            grid-gap: ${pxToRem(14)}rem;
            margin-top: ${pxToRem(30)}rem;
            grid-template-columns: repeat(2, 1fr);

            @media ${BREAKPOINTS.MD.max} {
              width: 100%;
              grid-template-columns: 1fr;
            }
          `}
        >
          <Button type="button" size="large" variant="secondary" onClick={props.onClose}>
            {t('device.order_req_modal.cancel_button')}
          </Button>

          <Button type="submit" size="large" loading={form.formState.isSubmitting}>
            {t('device.order_req_modal.submit_button')}
          </Button>
        </div>
      </form>
    </Fragment>
  )
}
